// src/services/crm/crmTimelineService.ts
import { useCallLogStore } from '@/stores/callLogStore'
import { useEmailStore } from '@/stores/emailStore'
import { useCommentStore } from '@/stores/commentStore'
import { useTaskStore } from '@/stores/taskStore'
import { useTimelineActivityStore } from '@/stores/timelineActivityStore'
import type { TimelineEvent } from '@/components/crm/CrmTimeline.vue'
import type { TimelineActivity } from '@/types/TimelineActivity'
import type { CallLog } from '@/types/CallLog'
import type { Email } from '@/types/EmailMessage'

/**
 * Convert a TimelineActivity to a TimelineEvent for display
 */
function activityToEvent(activity: TimelineActivity): TimelineEvent {
  return {
    id: activity.id!,
    date: activity.createdAt,
    type: activity.type,
    title: activity.title,
    description: activity.description,
    tags: activity.tags,
    organizationId: activity.organizationId,
  }
}

/**
 * Get timeline events for a specific person
 */
export async function getTimelineForPerson(personId: string): Promise<TimelineEvent[]> {
  const timelineStore = useTimelineActivityStore()
  await timelineStore.load()

  const activities = timelineStore.findBy(
    (activity: TimelineActivity) => activity.personId === personId,
  )

  return activities
    .map(activityToEvent)
    .sort(
      (a: TimelineEvent, b: TimelineEvent) =>
        new Date(b.date).getTime() - new Date(a.date).getTime(),
    )
}

/**
 * Get timeline events for a specific organization
 */
export async function getTimelineForOrganization(organizationId: string): Promise<TimelineEvent[]> {
  const timelineStore = useTimelineActivityStore()
  await timelineStore.load()

  const activities = timelineStore.findBy(
    (activity: TimelineActivity) => activity.organizationId === organizationId,
  )

  return activities
    .map(activityToEvent)
    .sort(
      (a: TimelineEvent, b: TimelineEvent) =>
        new Date(b.date).getTime() - new Date(a.date).getTime(),
    )
}

/**
 * Get global timeline events, optionally filtered by user
 */
export async function getGlobalTimeline(userId?: string): Promise<TimelineEvent[]> {
  const timelineStore = useTimelineActivityStore()
  await timelineStore.load()

  const activities = userId
    ? timelineStore.findBy((activity: TimelineActivity) => activity.userId === userId)
    : timelineStore.items

  return activities
    .map(activityToEvent)
    .sort(
      (a: TimelineEvent, b: TimelineEvent) =>
        new Date(b.date).getTime() - new Date(a.date).getTime(),
    )
}

/**
 * Legacy implementation for backwards compatibility
 * @deprecated Use the new timeline activity store instead
 */
export async function getLegacyTimelineForPerson(personId: string): Promise<TimelineEvent[]> {
  const callLogStore = useCallLogStore()
  await callLogStore.load()

  const calls = callLogStore.findBy((call: CallLog) => call.personId === personId)

  const callEvents: TimelineEvent[] = calls.map((call: CallLog) => ({
    id: call.id!,
    date: call.timestamp,
    type: 'call',
    title: `${call.direction === 'incoming' ? 'Incoming' : 'Outgoing'} call`,
    description: call.notes,
    tags: call.tags,
  }))

  return callEvents.sort(
    (a: TimelineEvent, b: TimelineEvent) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  )
}

/**
 * Legacy implementation for backwards compatibility
 * @deprecated Use the new timeline activity store instead
 */
export async function getLegacyTimelineForOrganization(
  organizationId: string,
): Promise<TimelineEvent[]> {
  const callLogStore = useCallLogStore()
  const emailStore = useEmailStore()

  await Promise.all([callLogStore.load(), emailStore.load()])

  const calls = callLogStore.findBy((call: CallLog) => call.organizationId === organizationId)
  const emails = emailStore.findBy((email: Email) => email.organizationId === organizationId)

  const callEvents: TimelineEvent[] = calls.map((call: CallLog) => ({
    id: call.id!,
    date: call.timestamp,
    type: 'call',
    title: `${call.direction === 'incoming' ? 'Incoming' : 'Outgoing'} call`,
    description: call.notes,
    tags: call.tags,
  }))

  const emailEvents: TimelineEvent[] = emails.map((email: Email) => ({
    id: email.id!,
    date: email.sentAt || email.createdAt!,
    type: 'email',
    title: email.message.subject || 'Email',
    description: email.message.text || '',
    tags: [],
  }))

  return [...callEvents, ...emailEvents].sort(
    (a: TimelineEvent, b: TimelineEvent) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  )
}
